import { useQuery } from '@tanstack/react-query';

import { fetchMasterList, fetchMasterLookup } from '../../account/api/master.api';

// Thin wrappers around the generic master-list fetcher for the lookups the Project form/tabs
// need — ProjectType (§5a), PoC/Production Funding Status (§5d). Query keys are `[prefix,
// 'list']`, matching useMasterCrud's `listKey` exactly (apps/web/src/features/account/hooks/
// useMasterCrud.ts) — the admin CRUD page for each master (e.g. ProjectTypePage via
// MasterDataPage) invalidates that same key on create/update/deactivate/activate, so a change
// made there is reflected here immediately instead of waiting out the 5-minute default
// staleTime. A hook using a *different* key (e.g. just `[prefix]`) would silently read a stale
// cache entry that never gets invalidated by the admin page's mutations.
export function useProjectTypes() {
  return useQuery({
    queryKey: ['project-types', 'list'],
    queryFn: () => fetchMasterList('/api/v1/project-types'),
  });
}

export function usePocFundingStatuses() {
  return useQuery({
    queryKey: ['project-poc-funding-statuses', 'list'],
    queryFn: () => fetchMasterList('/api/v1/project-poc-funding-statuses'),
  });
}

export function useProductionFundingStatuses() {
  return useQuery({
    queryKey: ['project-production-funding-statuses', 'list'],
    queryFn: () => fetchMasterList('/api/v1/project-production-funding-statuses'),
  });
}

export function useProjectFundingTypes() {
  return useQuery({
    queryKey: ['project-funding-types', 'list'],
    queryFn: () => fetchMasterList('/api/v1/project-funding-types'),
  });
}

// ── Active-only counterparts, for pickers ─────────────────────────────────────
// The hooks above return every record including deactivated ones — correct for list-page
// *filters* (you must still be able to filter by a since-retired Project Type) but wrong for a
// picker, which must not offer a deactivated record as a new selection. These hit each master's
// GET /lookup route instead (see MasterCrudService.findAllActive()). Keyed `[prefix, 'lookup']`,
// a sibling of the `[prefix, 'list']` key above — useMasterCrud invalidates the shared
// `[prefix]` root on every mutation, so both refresh the moment an admin deactivates or
// reactivates a record. `staleTime: 0` for the same reason useKeyAsks below uses it: form
// dialogs stay mounted for the page's lifetime, so without it a dialog re-opened after someone
// else deactivated a record would keep offering it from cache.
export function useProjectTypeLookup() {
  return useQuery({
    queryKey: ['project-types', 'lookup'],
    queryFn: () => fetchMasterLookup('/api/v1/project-types'),
    staleTime: 0,
  });
}

export function useProjectFundingTypeLookup() {
  return useQuery({
    queryKey: ['project-funding-types', 'lookup'],
    queryFn: () => fetchMasterLookup('/api/v1/project-funding-types'),
    staleTime: 0,
  });
}

export function usePocFundingStatusLookup() {
  return useQuery({
    queryKey: ['project-poc-funding-statuses', 'lookup'],
    queryFn: () => fetchMasterLookup('/api/v1/project-poc-funding-statuses'),
    staleTime: 0,
  });
}

export function useProductionFundingStatusLookup() {
  return useQuery({
    queryKey: ['project-production-funding-statuses', 'lookup'],
    queryFn: () => fetchMasterLookup('/api/v1/project-production-funding-statuses'),
    staleTime: 0,
  });
}

export function useEscalationCategoryLookup() {
  return useQuery({
    queryKey: ['escalation-categories', 'lookup'],
    queryFn: () => fetchMasterLookup('/api/v1/escalation-categories'),
    staleTime: 0,
  });
}

// Used by the Project Request form's Key Asks checkbox list (apps/web/src/features/presales) —
// lives here rather than in presales/ since every other master lookup used by a form outside
// its own admin domain follows this same "thin wrapper in delivery/hooks" convention.
// Unlike the lookups above, this one takes an `enabled` gate (the caller passes its dialog's
// `open` state) and uses `staleTime: 0`. The form dialog stays mounted for the page's whole
// lifetime — only its visibility toggles — so a plain useQuery would fetch once on page load and
// then serve that cached list for the rest of the 5-minute default staleTime, even across dialog
// re-opens. A Key Ask deactivated by anyone else in the meantime would keep showing as selectable
// long after it should have disappeared. Gating on `open` with `staleTime: 0` makes every dialog
// open a fresh fetch instead.
export function useKeyAsks(enabled = true) {
  return useQuery({
    queryKey: ['key-asks', 'list'],
    queryFn: () => fetchMasterList('/api/v1/key-asks'),
    enabled,
    staleTime: 0,
  });
}
